import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  MinusIcon,
} from 'lucide-react';
import React, { type ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type {
  ChildrenVariant,
  NavItem,
  SidebarState,
  SidebarVariant,
} from './types';

interface MainSidebarProps {
  items: NavItem[];
  state: SidebarState;
  onToggle: () => void;
  header: (isCollapsed: boolean) => ReactNode;
  footer: (isCollapsed: boolean) => ReactNode;
  activeItemId?: string; // For highlighting parent when child is active
  variant: SidebarVariant;
  childrenVariant?: ChildrenVariant;
  isCollapsible: boolean;
}

const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 60;
const HOVERING_ANIMATION_DISTANCE = 4;

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { x: -20, opacity: 0 },
  show: { x: 0, opacity: 1, transition: { duration: 0.3 } },
};

export const MainSidebar: React.FC<MainSidebarProps> = ({
  items,
  state,
  onToggle,
  header,
  footer,
  activeItemId,
  childrenVariant = 'panel',
  variant,
  isCollapsible,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isCollapsed = state === 'collapsed';

  // State to track expanded items in 'tree' mode
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {}
  );

  // State for toggle button hover
  const [isHoveringToggle, setIsHoveringToggle] = useState(false);

  const handleItemClick = (item: NavItem) => {
    if (childrenVariant === 'tree' && item.children) {
      if (isCollapsed) {
        onToggle(); // Auto-expand sidebar if collapsed
      }
      setExpandedItems((prev) => ({
        ...prev,
        [item.id]: !prev[item.id],
      }));
    } else if (item.path) {
      navigate(item.path);
    }
    // If panel mode, AppSidebar handles showing the sub-sidebar based on route/active state
  };

  // Calculate dynamic width based on hover state
  const getAnimationDistance = () => {
    if (state === 'hidden') return 0;

    if (!isHoveringToggle) return 0;

    return isCollapsed
      ? -HOVERING_ANIMATION_DISTANCE
      : HOVERING_ANIMATION_DISTANCE;
  };

  const getStaticSidebarHeight = () => {
    if (state === 'hidden') return 0;
    return isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: getStaticSidebarHeight(),
        bgcolor: 'background.paper',
        zIndex: 1200, // Above SubSidebar
        borderRadius: 0,
        position: 'relative',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shorter,
        }),
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* animated border */}
        <Box
          sx={{
            width: 0,
            position: 'absolute',
            top: 0,
            right: getAnimationDistance(),
            height: '100%',
            zIndex: 1000,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            transition: theme.transitions.create('right', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shorter,
            }),
            pointerEvents: 'none',
          }}
        ></Box>
        {/* Sidebar Toggle Button */}
        <Box
          onClick={() => {
            onToggle();
            setIsHoveringToggle(false);
          }}
          onMouseEnter={() => setIsHoveringToggle(true)}
          onMouseLeave={() => setIsHoveringToggle(false)}
          sx={{
            position: 'absolute',
            right: -22,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1300,
            width: 30,
            height: 30,
            borderRadius: '50%',
            display:
              variant === 'collapsible' && isCollapsible ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'text.secondary',
            transition: theme.transitions.create(
              ['background-color', 'color', 'transform'],
              {
                duration: theme.transitions.duration.shorter,
              }
            ),
            '&:hover': {
              color: 'text.primary',
            },
          }}
        >
          {isHoveringToggle ? (
            isCollapsed ? (
              <ChevronRightIcon size={18} strokeWidth={2.5} />
            ) : (
              <ChevronLeftIcon size={18} strokeWidth={2.5} />
            )
          ) : (
            // Pipe icon (vertical line)
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: 'rotate(90deg)',
              }}
            >
              <MinusIcon size={18} strokeWidth={2.5} />
            </Box>
          )}
        </Box>

        {/* Branding Header */}
        {header(isCollapsed)}

        {/* Navigation Items */}
        <List sx={{ px: 1, flex: 1, overflowX: 'hidden' }}>
          {' '}
          {/* Ensure internal content clips */}
          {items.map((item) => {
            const isSelected =
              item.path === location.pathname || activeItemId === item.id;
            const isItemExpanded = expandedItems[item.id];
            const hasChildren = item.children && item.children.length > 0;

            const ItemContent = (
              <ListItemButton
                selected={isSelected}
                onClick={() => handleItemClick(item)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  justifyContent: isCollapsed ? 'center' : 'initial',
                  px: 2.5,
                  minHeight: 48,
                  py: 0.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isCollapsed ? 'auto' : 2,
                    justifyContent: 'center',
                    color: isSelected ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    opacity: isCollapsed ? 0 : 1,
                    display: isCollapsed ? 'none' : 'block',
                  }}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: isSelected ? 600 : 400,
                  }}
                />
                {childrenVariant === 'tree' && !isCollapsed && hasChildren && (
                  <Box component="span" sx={{ ml: 1, display: 'flex' }}>
                    {isItemExpanded ? (
                      <ChevronUpIcon size={16} />
                    ) : (
                      <ChevronDownIcon size={16} />
                    )}
                  </Box>
                )}
              </ListItemButton>
            );

            return (
              <React.Fragment key={item.id}>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  {isCollapsed ? (
                    <Tooltip title={item.label} placement="right">
                      {ItemContent}
                    </Tooltip>
                  ) : (
                    ItemContent
                  )}
                </ListItem>

                {/* Tree View Children */}
                {childrenVariant === 'tree' && !isCollapsed && hasChildren && (
                  <Collapse in={isItemExpanded} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        position: 'relative',
                        ml: 3.5,
                        mb: 0.5,
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <List
                        component={motion.ul}
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                        disablePadding
                        sx={{
                          m: 0,
                          p: 0,
                          listStyle: 'none',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        {item.children!.map((child) => {
                          const isChildSelected =
                            child.path === location.pathname;
                          return (
                            <ListItem
                              key={child.id}
                              disablePadding
                              component={motion.li}
                              variants={itemVariants}
                            >
                              <ListItemButton
                                selected={isChildSelected}
                                onClick={() => navigate(child.path)}
                                sx={{
                                  pl: 2, // Indent text from the line
                                  py: 1,
                                  ml: 1,
                                  position: 'relative',
                                  borderRadius: 2,
                                }}
                              >
                                <ListItemText
                                  primary={child.label}
                                  primaryTypographyProps={{
                                    variant: 'caption',
                                    fontWeight: isChildSelected ? 500 : 400,
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>

        {footer(isCollapsed)}
      </Box>
    </Paper>
  );
};
